// src/pages/Invoices/InvoiceLayoutDialog.jsx — تخصيص مظهر شاشة الفاتورة (لكل نوع فاتورة في الشركة)
import React, { useEffect, useState } from "react";
import {
  Box, Typography, Button, IconButton, Switch, Dialog, DialogContent, DialogActions, CircularProgress, Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TuneIcon from "@mui/icons-material/Tune";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

// ── التعريفات المشتركة مع InvoiceForm ──
// حقول الرأس القابلة للإخفاء (الحقول اللازمة للحفظ مثل المستودع وطريقة الدفع والعميل لا تُخفى)
export const DEFAULT_HEADER_FIELDS = { Notes: true, InvoiceStatus: true, Reference: true, CustomerPrevBalance: true };

// أعمدة السطور — الصنف والكمية والسعر لا تُخفى
export const DEFAULT_ITEM_FIELDS = {
  ProductId: true, Description: true, Unit: true, Quantity: true, UnitPrice: true,
  LineTotal: true, Discount: true, TaxPercent: true, Net: true,
};
export const LOCKED_ITEM_FIELDS = ["ProductId", "Quantity", "UnitPrice"];
export const DEFAULT_ITEM_ORDER = Object.keys(DEFAULT_ITEM_FIELDS);

// العرض الافتراضي لكل عمود بالبكسل (null = تلقائي: يأخذ المساحة المتبقية)
export const DEFAULT_ITEM_WIDTHS = {
  ProductId: null, Description: null, Unit: 110, Quantity: 100, UnitPrice: 110,
  LineTotal: 100, Discount: 100, TaxPercent: 90, Net: 110,
};
export const MIN_COL_WIDTH = 60;
export const MAX_COL_WIDTH = 500;
const clampWidth = (v) => {
  const n = parseInt(v, 10);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.min(MAX_COL_WIDTH, Math.max(MIN_COL_WIDTH, n));
};

/** XML أعمدة السطور: الظهور + الترتيب (بترتيب العناصر) + العرض */
export function buildItemsLayoutXml(visible, order, widths) {
  return `<Layout>${order
    .map((name, i) => {
      const w = clampWidth(widths?.[name]);
      return `<Field name='${escAttr(name)}' visible='${visible[name] ? "true" : "false"}' order='${i + 1}'${w ? ` width='${w}'` : ""} />`;
    })
    .join("")}</Layout>`;
}

/** قراءة أعمدة السطور: { visible, order, widths } — الأعمدة الجديدة غير المحفوظة تُضاف في آخر الترتيب */
export function parseItemsLayoutXml(xml) {
  const visible = { ...DEFAULT_ITEM_FIELDS };
  const widths = { ...DEFAULT_ITEM_WIDTHS };
  let order = [...DEFAULT_ITEM_ORDER];
  if (xml) {
    try {
      const doc = new DOMParser().parseFromString(xml, "text/xml");
      if (!doc.querySelector("parsererror")) {
        const saved = [];
        doc.querySelectorAll("Field").forEach((f, idx) => {
          const name = f.getAttribute("name");
          if (!(name in visible)) return;
          visible[name] = f.getAttribute("visible") === "true";
          if (f.hasAttribute("width")) widths[name] = clampWidth(f.getAttribute("width"));
          saved.push({ name, order: parseInt(f.getAttribute("order"), 10) || idx + 1 });
        });
        if (saved.length) {
          const known = saved.sort((a, b) => a.order - b.order).map(x => x.name);
          order = [...known, ...DEFAULT_ITEM_ORDER.filter(k => !known.includes(k))];
        }
      }
    } catch {
      // الافتراضي
    }
  }
  LOCKED_ITEM_FIELDS.forEach((k) => { visible[k] = true; });
  return { visible, order, widths };
}

// أسماء قديمة كانت تُحفظ في الرأس
const HEADER_ALIASES = { ReferenceNumber: "Reference", ReferenceDate: "Reference" };

const escAttr = (v) => String(v ?? "").replace(/&/g, "&amp;").replace(/'/g, "&apos;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

export function buildLayoutXml(fields) {
  return `<Layout>${Object.entries(fields)
    .map(([name, vis], i) => `<Field name='${escAttr(name)}' visible='${vis ? "true" : "false"}' order='${i + 1}' />`)
    .join("")}</Layout>`;
}

/** يقرأ XML المحفوظ ويطبّقه على الافتراضي — المفاتيح غير المعروفة تُتجاهل */
export function parseLayoutXml(xml, defaults, { aliases = {}, locked = [] } = {}) {
  const out = { ...defaults };
  if (!xml) return out;
  try {
    const doc = new DOMParser().parseFromString(xml, "text/xml");
    if (doc.querySelector("parsererror")) return out;
    doc.querySelectorAll("Field").forEach((f) => {
      const raw = f.getAttribute("name");
      const name = aliases[raw] || raw;
      if (name in out) out[name] = f.getAttribute("visible") === "true";
    });
  } catch {
    return { ...defaults };
  }
  locked.forEach((k) => { out[k] = true; });
  return out;
}

// ألوان صريحة للأزرار: ثيم التطبيق يغيّر الأزرار النصية فيصبح النص بلون الخلفية
const primaryBtnSx = {
  fontWeight: 800, borderRadius: "10px", px: 2, boxShadow: "none", bgcolor: "#4f46e5", color: "#fff",
  "&:hover": { bgcolor: "#4338ca", boxShadow: "none" },
  "&.Mui-disabled": { bgcolor: "#c7d2fe", color: "#fff" },
  "& .MuiButton-startIcon": { ml: .5, mr: -.25 },
};
const secondaryBtnSx = {
  fontWeight: 700, borderRadius: "10px", px: 2, bgcolor: "#fff", color: "#475569", border: "1px solid #e2e8f0",
  "&:hover": { bgcolor: "#f8fafc", borderColor: "#cbd5e1" },
  "&.Mui-disabled": { bgcolor: "#fff", color: "#cbd5e1", borderColor: "#f1f5f9" },
  "& .MuiButton-startIcon": { ml: .5, mr: -.25 },
};

const Section = ({ title, hint, children }) => (
  <Box sx={{ mb: 2 }}>
    <Typography sx={{ fontWeight: 800, fontSize: 14, color: "#0f172a" }}>{title}</Typography>
    {hint && <Typography sx={{ fontSize: 12, color: "#94a3b8", mb: 1 }}>{hint}</Typography>}
    <Box sx={{ border: "1px solid #e5e7eb", borderRadius: "12px", overflow: "hidden" }}>{children}</Box>
  </Box>
);

const Row = ({ label, checked, locked, onChange }) => (
  <Box
    onClick={() => !locked && onChange(!checked)}
    sx={{
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, px: 1.5, py: .5,
      borderBottom: "1px solid #f1f5f9", "&:last-of-type": { borderBottom: 0 },
      cursor: locked ? "default" : "pointer", "&:hover": { bgcolor: locked ? "transparent" : "#f8fafc" },
    }}
  >
    <Typography sx={{ fontWeight: 700, fontSize: 14, color: checked ? "#0f172a" : "#94a3b8" }}>{label}</Typography>
    {locked ? (
      <Tooltip title="حقل أساسي لا يُخفى">
        <Box sx={{ display: "flex", alignItems: "center", gap: .5, color: "#94a3b8", fontSize: 12, fontWeight: 700, py: 1 }}>
          <LockOutlinedIcon sx={{ fontSize: 16 }} /> دائمًا
        </Box>
      </Tooltip>
    ) : (
      <Switch size="small" checked={checked} onClick={(e) => e.stopPropagation()} onChange={(e) => onChange(e.target.checked)} />
    )}
  </Box>
);

const widthInputSx = {
  width: 74, height: 30, border: "1.5px solid #e2e8f0", borderRadius: 8, padding: "0 6px",
  fontSize: 13, fontWeight: 700, textAlign: "center", outline: "none", boxSizing: "border-box", fontFamily: "inherit",
};

const ColumnRow = ({ label, checked, locked, width, first, last, onToggle, onWidth, onMove }) => (
  <Box sx={{
    display: "flex", alignItems: "center", gap: .75, px: 1, py: .5,
    borderBottom: "1px solid #f1f5f9", "&:last-of-type": { borderBottom: 0 }, bgcolor: checked ? "#fff" : "#fafafa",
  }}>
    <DragIndicatorIcon sx={{ fontSize: 18, color: "#cbd5e1" }} />
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <IconButton size="small" disabled={first} onClick={() => onMove(-1)} sx={{ p: .1 }}><ArrowUpwardIcon sx={{ fontSize: 15 }} /></IconButton>
      <IconButton size="small" disabled={last} onClick={() => onMove(1)} sx={{ p: .1 }}><ArrowDownwardIcon sx={{ fontSize: 15 }} /></IconButton>
    </Box>
    <Typography sx={{ flex: 1, minWidth: 0, fontWeight: 700, fontSize: 14, color: checked ? "#0f172a" : "#94a3b8" }}>{label}</Typography>
    <Tooltip title="العرض بالبكسل — فارغ = تلقائي">
      <input
        type="text" inputMode="numeric" value={width ?? ""} placeholder="تلقائي" disabled={!checked}
        onChange={(e) => onWidth(e.target.value.replace(/[\u0660-\u0669]/g, d => String(d.charCodeAt(0) - 0x0660)).replace(/[^0-9]/g, ""))}
        style={{ ...widthInputSx, opacity: checked ? 1 : .5 }}
      />
    </Tooltip>
    {locked ? (
      <Tooltip title="عمود أساسي لا يُخفى">
        <Box sx={{ width: 40, display: "grid", placeItems: "center", color: "#94a3b8" }}><LockOutlinedIcon sx={{ fontSize: 17 }} /></Box>
      </Tooltip>
    ) : (
      <Switch size="small" checked={checked} onChange={(e) => onToggle(e.target.checked)} />
    )}
  </Box>
);

export default function InvoiceLayoutDialog({ open, typeName, headerFields, itemFields, itemOrder, itemWidths, headerLabels, itemLabels, onClose, onSave }) {
  const [h, setH] = useState(headerFields);
  const [it, setIt] = useState(itemFields);
  // بدون itemOrder/itemWidths (نسخة InvoiceForm أقدم): الترتيب والعرض الافتراضيان
  const [order, setOrder] = useState(itemOrder || DEFAULT_ITEM_ORDER);
  const [widths, setWidths] = useState(itemWidths || DEFAULT_ITEM_WIDTHS);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setH(headerFields); setIt(itemFields); setOrder(itemOrder || DEFAULT_ITEM_ORDER); setWidths(itemWidths || DEFAULT_ITEM_WIDTHS); }
  }, [open, headerFields, itemFields, itemOrder, itemWidths]);

  const move = (idx, dir) => setOrder((o) => {
    const j = idx + dir;
    if (j < 0 || j >= o.length) return o;
    const next = [...o];
    [next[idx], next[j]] = [next[j], next[idx]];
    return next;
  });

  const save = async () => {
    setSaving(true);
    try {
      const cleanWidths = Object.fromEntries(order.map(k => [k, clampWidth(widths[k])]));
      const ok = await onSave(h, it, order, cleanWidths);
      if (ok) onClose();
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setH({ ...DEFAULT_HEADER_FIELDS });
    setIt({ ...DEFAULT_ITEM_FIELDS });
    setOrder([...DEFAULT_ITEM_ORDER]);
    setWidths({ ...DEFAULT_ITEM_WIDTHS });
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} dir="rtl" maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "18px", overflow: "hidden" } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 2.5, py: 1.75, bgcolor: "#4f46e5", color: "#fff" }}>
        <TuneIcon />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 16 }}>تخصيص المظهر</Typography>
          <Typography sx={{ fontSize: 12.5, opacity: .85 }}>{typeName ? `${typeName} — ` : ""}يُطبَّق على كل مستخدمي الشركة</Typography>
        </Box>
        <IconButton onClick={onClose} disabled={saving} size="small" sx={{ color: "#fff" }}><CloseIcon /></IconButton>
      </Box>

      <DialogContent sx={{ pt: "16px !important" }}>
        <Section title="رأس الفاتورة" hint="الحقول اللازمة للحفظ (التاريخ، المستودع، طريقة الدفع، العميل/المورد) تظهر دائمًا">
          {Object.keys(DEFAULT_HEADER_FIELDS).map((k) => (
            <Row key={k} label={headerLabels[k] || k} checked={!!h[k]} onChange={(v) => setH((p) => ({ ...p, [k]: v }))} />
          ))}
        </Section>
        <Section title="أعمدة السطور" hint="الترتيب من أول عمود (يمين الجدول) — العرض بالبكسل، فارغ = تلقائي">
          {order.map((k, i) => (
            <ColumnRow key={k} label={itemLabels[k] || k} checked={!!it[k]} locked={LOCKED_ITEM_FIELDS.includes(k)}
              width={widths[k]} first={i === 0} last={i === order.length - 1}
              onToggle={(v) => setIt((p) => ({ ...p, [k]: v }))}
              onWidth={(v) => setWidths((p) => ({ ...p, [k]: v === "" ? null : v }))}
              onMove={(d) => move(i, d)} />
          ))}
        </Section>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.25, borderTop: "1px solid #eef0f4", justifyContent: "space-between" }}>
        <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={reset} disabled={saving}
          sx={secondaryBtnSx}>
          الافتراضي
        </Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="outlined" onClick={onClose} disabled={saving} sx={secondaryBtnSx}>إلغاء</Button>
          <Button variant="contained" disableElevation onClick={save} disabled={saving}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
            sx={primaryBtnSx}>
            حفظ
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export { HEADER_ALIASES };